import { Invoice } from 'types'
import useUpdateInvoiceFields from './useUpdateInvoiceFields'
import { InvoiceFieldUpdate, InvoiceFieldMutationParams } from '../types'

/**
 * Factory that returns a hook pre-bound to a fixed `fields` payload.
 * Use this to create single-purpose field-update hooks without repeating
 * the onSuccess/onError wiring each time.
 *
 * All hooks produced by this factory share the same optimistic update logic
 * from useUpdateInvoiceFields — the only difference between useMarkAsPaid,
 * useMarkAsUnpaid, and useFinalizeInvoice is the fields payload they patch.
 *
 * @param fields    - The invoice fields to patch (e.g. `{ paid: true }`)
 * @param mutateKey - The name to give the `mutate` function in the returned object
 *
 * @example
 *   const useMarkAsPaid = createInvoiceFieldMutation({ paid: true }, 'markAsPaid')
 */
const createInvoiceFieldMutation = <K extends string>(
  fields: InvoiceFieldUpdate,
  mutateKey: K
) => {
  return ({ onSuccess, onError }: InvoiceFieldMutationParams): Record<K, (invoice: Invoice) => void> & { isPending: boolean } => {
    const { mutate, isPending } = useUpdateInvoiceFields({ fields, onSuccess, onError })
    return { [mutateKey]: mutate, isPending } as Record<K, (invoice: Invoice) => void> & { isPending: boolean }
  }
}

export default createInvoiceFieldMutation
